// Lekkie i18n dla stron klienta (login/register/forgot/reset/account).
// Start: pl, en, de. Język w cookie 'fablio_lang' (czytane też po stronie serwera).
export const LANGS = [
  "en",
  "pl",
  "de",
  "es",
  "fr",
  "it",
  "pt",
  "nl",
  "uk",
  "sv",
  "cs",
  "tr",
] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = "pl";
export const LANG_COOKIE = "fablio_lang";

export const LANG_META: Record<Lang, { flag: string; name: string }> = {
  en: { flag: "🇬🇧", name: "English" },
  pl: { flag: "🇵🇱", name: "Polski" },
  de: { flag: "🇩🇪", name: "Deutsch" },
  es: { flag: "🇪🇸", name: "Español" },
  fr: { flag: "🇫🇷", name: "Français" },
  it: { flag: "🇮🇹", name: "Italiano" },
  pt: { flag: "🇵🇹", name: "Português" },
  nl: { flag: "🇳🇱", name: "Nederlands" },
  uk: { flag: "🇺🇦", name: "Українська" },
  sv: { flag: "🇸🇪", name: "Svenska" },
  cs: { flag: "🇨🇿", name: "Čeština" },
  tr: { flag: "🇹🇷", name: "Türkçe" },
};

// Locale do formatowania dat itp.
export const LOCALE: Record<Lang, string> = {
  en: "en-GB",
  pl: "pl-PL",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  it: "it-IT",
  pt: "pt-PT",
  nl: "nl-NL",
  uk: "uk-UA",
  sv: "sv-SE",
  cs: "cs-CZ",
  tr: "tr-TR",
};

export function normalizeLang(v: string | undefined | null): Lang {
  return (LANGS as readonly string[]).includes(v ?? "")
    ? (v as Lang)
    : DEFAULT_LANG;
}

type Dict = Record<string, string>;

const pl: Dict = {
  "nav.account_panel": "Panel admina",
  "nav.user_panel": "Panel użytkownika",
  "nav.logout": "Wyloguj",
  "nav.choose_lang": "Wybierz język",

  "login.title": "Zaloguj się",
  "login.subtitle": "Witaj z powrotem w Fablio.",
  "login.google": "Kontynuuj z Google",
  "login.facebook": "Kontynuuj z Facebookiem",
  "login.or": "lub",
  "login.email": "E-mail",
  "login.password": "Hasło",
  "login.forgot": "Zapomniałeś hasła?",
  "login.submit": "Zaloguj",
  "login.submitting": "Logowanie…",
  "login.no_account": "Nie masz konta?",
  "login.register": "Załóż konto",
  "login.error": "Niepoprawny e-mail lub hasło",

  "register.title": "Załóż konto",
  "register.children_count": "Ile masz dzieci?",
  "register.subtitle": "Twoje bajki i subskrypcje w jednym miejscu.",
  "register.name": "Imię (opcjonalnie)",
  "register.email": "E-mail",
  "register.password": "Hasło (min. 8 znaków)",
  "register.submit": "Załóż konto",
  "register.submitting": "Tworzenie konta…",
  "register.has_account": "Masz już konto?",
  "register.login": "Zaloguj się",

  "forgot.title": "Reset hasła",
  "forgot.subtitle": "Podaj e-mail — wyślemy link do ustawienia nowego hasła.",
  "forgot.email": "E-mail",
  "forgot.submit": "Wyślij link",
  "forgot.submitting": "Wysyłanie…",
  "forgot.sent":
    "Jeśli konto z tym e-mailem istnieje, wysłaliśmy link do resetu hasła. Sprawdź skrzynkę (i spam).",
  "forgot.back": "← Powrót do logowania",

  "reset.title": "Nowe hasło",
  "reset.no_token": "Brak tokenu w linku.",
  "reset.request_new": "Poproś o nowy",
  "reset.changed": "✓ Hasło zostało zmienione.",
  "reset.login": "Zaloguj się",
  "reset.next": "Nowe hasło (min. 8 znaków)",
  "reset.confirm": "Powtórz nowe hasło",
  "reset.submit": "Ustaw nowe hasło",
  "reset.submitting": "Zapisywanie…",

  "account.greeting": "Cześć{name}! 👋",
  "account.subtitle": "Twoje spersonalizowane bajki w jednym miejscu.",
  "account.my_stories": "Moje bajki",
  "account.no_stories": "Nie masz jeszcze żadnych bajek.",
  "account.create_first": "✨ Stwórz pierwszą bajkę",
  "account.listen": "▶ Posłuchaj",
  "account.download": "Pobierz",
  "account.open_book": "📖 Otwórz książkę",
  "account.watch_video": "🎬 Obejrzyj",
  "account.make_video": "🎬 Generuj wideo",
  "account.making": "Tworzę wideo…",
  "account.soon": "wkrótce",
  "account.reuse": "Użyj ponownie / Edytuj",
  "account.status.pending": "Oczekuje na płatność",
  "account.status.paid": "W przygotowaniu",
  "account.status.fulfilled": "Gotowe",
  "account.status.failed": "Błąd",

  "password.set_title": "Ustaw hasło",
  "password.change_title": "Zmień hasło",
  "password.set_desc":
    "Twoje konto zostało utworzone przez Google. Ustaw hasło, aby móc logować się też e-mailem i hasłem.",
  "password.change_desc": "Hasło jest przechowywane jako bcrypt-hash.",
  "password.current": "Obecne hasło",
  "password.next": "Nowe hasło (min. 8 znaków)",
  "password.confirm": "Powtórz nowe hasło",
  "password.set_btn": "Ustaw hasło",
  "password.change_btn": "Zmień hasło",
  "password.saving": "Zapisywanie…",
  "password.saved": "✓ Hasło zapisane.",

  "credits.title": "Moje kredyty",
  "credits.subtitle": "Bajki z pakietów i subskrypcji — twórz, kiedy chcesz.",
  "credits.none": "Nie masz jeszcze kredytów. Kup pakiet lub subskrypcję.",
  "credits.create": "Stwórz bajkę",
  "tier.audio": "Bajka audio",
  "tier.illustrated": "Książka ilustrowana",
  "tier.printed": "Druk",

  "errors.invalid_email": "Niepoprawny e-mail",
  "errors.password_min8": "Hasło musi mieć min. 8 znaków",
  "errors.email_exists": "Konto z tym e-mailem już istnieje",
  "errors.passwords_mismatch": "Hasła nie są identyczne",
  "errors.no_token": "Brak tokenu",
  "errors.link_expired": "Link wygasł lub jest niepoprawny. Poproś o nowy.",
  "errors.no_session": "Brak sesji",
  "errors.no_account": "Brak konta",
  "errors.current_wrong": "Obecne hasło jest niepoprawne",
};

const en: Dict = {
  "nav.account_panel": "Admin panel",
  "nav.user_panel": "User panel",
  "nav.logout": "Sign out",
  "nav.choose_lang": "Choose language",

  "login.title": "Sign in",
  "login.subtitle": "Welcome back to Fablio.",
  "login.google": "Continue with Google",
  "login.facebook": "Continue with Facebook",
  "login.or": "or",
  "login.email": "E-mail",
  "login.password": "Password",
  "login.forgot": "Forgot your password?",
  "login.submit": "Sign in",
  "login.submitting": "Signing in…",
  "login.no_account": "Don't have an account?",
  "login.register": "Create account",
  "login.error": "Incorrect e-mail or password",

  "register.title": "Create account",
  "register.children_count": "How many children?",
  "register.subtitle": "Your stories and subscriptions in one place.",
  "register.name": "Name (optional)",
  "register.email": "E-mail",
  "register.password": "Password (min. 8 characters)",
  "register.submit": "Create account",
  "register.submitting": "Creating account…",
  "register.has_account": "Already have an account?",
  "register.login": "Sign in",

  "forgot.title": "Reset password",
  "forgot.subtitle": "Enter your e-mail — we'll send a link to set a new password.",
  "forgot.email": "E-mail",
  "forgot.submit": "Send link",
  "forgot.submitting": "Sending…",
  "forgot.sent":
    "If an account with this e-mail exists, we've sent a password reset link. Check your inbox (and spam).",
  "forgot.back": "← Back to sign in",

  "reset.title": "New password",
  "reset.no_token": "Missing token in the link.",
  "reset.request_new": "Request a new one",
  "reset.changed": "✓ Your password has been changed.",
  "reset.login": "Sign in",
  "reset.next": "New password (min. 8 characters)",
  "reset.confirm": "Repeat new password",
  "reset.submit": "Set new password",
  "reset.submitting": "Saving…",

  "account.greeting": "Hi{name}! 👋",
  "account.subtitle": "Your personalized stories in one place.",
  "account.my_stories": "My stories",
  "account.no_stories": "You don't have any stories yet.",
  "account.create_first": "✨ Create your first story",
  "account.listen": "▶ Listen",
  "account.download": "Download",
  "account.open_book": "📖 Open book",
  "account.watch_video": "🎬 Watch",
  "account.make_video": "🎬 Make video",
  "account.making": "Making video…",
  "account.soon": "soon",
  "account.reuse": "Reuse / Edit",
  "account.status.pending": "Awaiting payment",
  "account.status.paid": "In preparation",
  "account.status.fulfilled": "Ready",
  "account.status.failed": "Error",

  "password.set_title": "Set password",
  "password.change_title": "Change password",
  "password.set_desc":
    "Your account was created with Google. Set a password to also sign in with e-mail and password.",
  "password.change_desc": "The password is stored only as a bcrypt hash.",
  "password.current": "Current password",
  "password.next": "New password (min. 8 characters)",
  "password.confirm": "Repeat new password",
  "password.set_btn": "Set password",
  "password.change_btn": "Change password",
  "password.saving": "Saving…",
  "password.saved": "✓ Password saved.",

  "credits.title": "My credits",
  "credits.subtitle": "Stories from packs and subscriptions — create whenever you like.",
  "credits.none": "You don't have any credits yet. Buy a pack or subscription.",
  "credits.create": "Create a story",
  "tier.audio": "Audio Story",
  "tier.illustrated": "Illustrated Book",
  "tier.printed": "Printed",

  "errors.invalid_email": "Invalid e-mail",
  "errors.password_min8": "Password must be at least 8 characters",
  "errors.email_exists": "An account with this e-mail already exists",
  "errors.passwords_mismatch": "Passwords don't match",
  "errors.no_token": "Missing token",
  "errors.link_expired": "The link has expired or is invalid. Request a new one.",
  "errors.no_session": "No session",
  "errors.no_account": "No account",
  "errors.current_wrong": "Current password is incorrect",
};

const de: Dict = {
  "nav.account_panel": "Admin-Panel",
  "nav.user_panel": "Benutzerbereich",
  "nav.logout": "Abmelden",
  "nav.choose_lang": "Sprache wählen",

  "login.title": "Anmelden",
  "login.subtitle": "Willkommen zurück bei Fablio.",
  "login.google": "Mit Google fortfahren",
  "login.facebook": "Mit Facebook fortfahren",
  "login.or": "oder",
  "login.email": "E-Mail",
  "login.password": "Passwort",
  "login.forgot": "Passwort vergessen?",
  "login.submit": "Anmelden",
  "login.submitting": "Anmeldung…",
  "login.no_account": "Noch kein Konto?",
  "login.register": "Konto erstellen",
  "login.error": "Falsche E-Mail oder falsches Passwort",

  "register.title": "Konto erstellen",
  "register.subtitle": "Deine Geschichten und Abos an einem Ort.",
  "register.name": "Name (optional)",
  "register.email": "E-Mail",
  "register.password": "Passwort (mind. 8 Zeichen)",
  "register.submit": "Konto erstellen",
  "register.submitting": "Konto wird erstellt…",
  "register.has_account": "Schon ein Konto?",
  "register.login": "Anmelden",

  "forgot.title": "Passwort zurücksetzen",
  "forgot.subtitle":
    "Gib deine E-Mail ein — wir senden dir einen Link zum Festlegen eines neuen Passworts.",
  "forgot.email": "E-Mail",
  "forgot.submit": "Link senden",
  "forgot.submitting": "Senden…",
  "forgot.sent":
    "Falls ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet. Prüfe dein Postfach (und Spam).",
  "forgot.back": "← Zurück zur Anmeldung",

  "reset.title": "Neues Passwort",
  "reset.no_token": "Kein Token im Link.",
  "reset.request_new": "Neuen anfordern",
  "reset.changed": "✓ Dein Passwort wurde geändert.",
  "reset.login": "Anmelden",
  "reset.next": "Neues Passwort (mind. 8 Zeichen)",
  "reset.confirm": "Neues Passwort wiederholen",
  "reset.submit": "Neues Passwort festlegen",
  "reset.submitting": "Speichern…",

  "account.greeting": "Hallo{name}! 👋",
  "account.subtitle": "Deine personalisierten Geschichten an einem Ort.",
  "account.my_stories": "Meine Geschichten",
  "account.no_stories": "Du hast noch keine Geschichten.",
  "account.create_first": "✨ Erste Geschichte erstellen",
  "account.listen": "▶ Anhören",
  "account.status.pending": "Wartet auf Zahlung",
  "account.status.paid": "In Vorbereitung",
  "account.status.fulfilled": "Fertig",
  "account.status.failed": "Fehler",

  "password.set_title": "Passwort festlegen",
  "password.change_title": "Passwort ändern",
  "password.set_desc":
    "Dein Konto wurde mit Google erstellt. Lege ein Passwort fest, um dich auch mit E-Mail und Passwort anzumelden.",
  "password.change_desc": "Das Passwort wird nur als bcrypt-Hash gespeichert.",
  "password.current": "Aktuelles Passwort",
  "password.next": "Neues Passwort (mind. 8 Zeichen)",
  "password.confirm": "Neues Passwort wiederholen",
  "password.set_btn": "Passwort festlegen",
  "password.change_btn": "Passwort ändern",
  "password.saving": "Speichern…",
  "password.saved": "✓ Passwort gespeichert.",

  "credits.title": "Meine Guthaben",
  "credits.subtitle": "Geschichten aus Paketen und Abos — erstelle sie, wann du willst.",
  "credits.none": "Du hast noch keine Guthaben. Kaufe ein Paket oder Abo.",
  "credits.create": "Geschichte erstellen",
  "tier.audio": "Audio-Geschichte",
  "tier.illustrated": "Illustriertes Buch",
  "tier.printed": "Druck",

  "errors.invalid_email": "Ungültige E-Mail",
  "errors.password_min8": "Passwort muss mind. 8 Zeichen haben",
  "errors.email_exists": "Ein Konto mit dieser E-Mail existiert bereits",
  "errors.passwords_mismatch": "Passwörter stimmen nicht überein",
  "errors.no_token": "Token fehlt",
  "errors.link_expired": "Der Link ist abgelaufen oder ungültig. Fordere einen neuen an.",
  "errors.no_session": "Keine Sitzung",
  "errors.no_account": "Kein Konto",
  "errors.current_wrong": "Aktuelles Passwort ist falsch",
};

const es: Dict = {
  "nav.account_panel": "Panel de administración",
  "nav.user_panel": "Panel de usuario",
  "nav.logout": "Cerrar sesión",
  "nav.choose_lang": "Elige idioma",
  "login.title": "Iniciar sesión",
  "login.subtitle": "Bienvenido de nuevo a Fablio.",
  "login.google": "Continuar con Google",
  "login.facebook": "Continuar con Facebook",
  "login.or": "o",
  "login.email": "Correo electrónico",
  "login.password": "Contraseña",
  "login.forgot": "¿Olvidaste tu contraseña?",
  "login.submit": "Iniciar sesión",
  "login.submitting": "Iniciando sesión…",
  "login.no_account": "¿No tienes cuenta?",
  "login.register": "Crear cuenta",
  "login.error": "Correo o contraseña incorrectos",
  "register.title": "Crear cuenta",
  "register.subtitle": "Tus cuentos y suscripciones en un solo lugar.",
  "register.name": "Nombre (opcional)",
  "register.email": "Correo electrónico",
  "register.password": "Contraseña (mín. 8 caracteres)",
  "register.submit": "Crear cuenta",
  "register.submitting": "Creando cuenta…",
  "register.has_account": "¿Ya tienes cuenta?",
  "register.login": "Iniciar sesión",
  "forgot.title": "Restablecer contraseña",
  "forgot.subtitle":
    "Introduce tu correo: te enviaremos un enlace para crear una nueva contraseña.",
  "forgot.email": "Correo electrónico",
  "forgot.submit": "Enviar enlace",
  "forgot.submitting": "Enviando…",
  "forgot.sent":
    "Si existe una cuenta con este correo, hemos enviado un enlace para restablecer la contraseña. Revisa tu bandeja (y el spam).",
  "forgot.back": "← Volver a iniciar sesión",
  "reset.title": "Nueva contraseña",
  "reset.no_token": "Falta el token en el enlace.",
  "reset.request_new": "Solicita uno nuevo",
  "reset.changed": "✓ Tu contraseña ha sido cambiada.",
  "reset.login": "Iniciar sesión",
  "reset.next": "Nueva contraseña (mín. 8 caracteres)",
  "reset.confirm": "Repite la nueva contraseña",
  "reset.submit": "Establecer nueva contraseña",
  "reset.submitting": "Guardando…",
  "account.greeting": "¡Hola{name}! 👋",
  "account.subtitle": "Tus cuentos personalizados en un solo lugar.",
  "account.my_stories": "Mis cuentos",
  "account.no_stories": "Aún no tienes cuentos.",
  "account.create_first": "✨ Crea tu primer cuento",
  "account.listen": "▶ Escuchar",
  "account.status.pending": "Pendiente de pago",
  "account.status.paid": "En preparación",
  "account.status.fulfilled": "Listo",
  "account.status.failed": "Error",
  "password.set_title": "Establecer contraseña",
  "password.change_title": "Cambiar contraseña",
  "password.set_desc":
    "Tu cuenta se creó con Google. Establece una contraseña para iniciar sesión también con correo y contraseña.",
  "password.change_desc": "La contraseña se guarda solo como hash bcrypt.",
  "password.current": "Contraseña actual",
  "password.next": "Nueva contraseña (mín. 8 caracteres)",
  "password.confirm": "Repite la nueva contraseña",
  "password.set_btn": "Establecer contraseña",
  "password.change_btn": "Cambiar contraseña",
  "password.saving": "Guardando…",
  "password.saved": "✓ Contraseña guardada.",
  "errors.invalid_email": "Correo electrónico no válido",
  "errors.password_min8": "La contraseña debe tener al menos 8 caracteres",
  "errors.email_exists": "Ya existe una cuenta con este correo",
  "errors.passwords_mismatch": "Las contraseñas no coinciden",
  "errors.no_token": "Falta el token",
  "errors.link_expired": "El enlace ha caducado o no es válido. Solicita uno nuevo.",
  "errors.no_session": "Sin sesión",
  "errors.no_account": "Sin cuenta",
  "errors.current_wrong": "La contraseña actual es incorrecta",
};

const fr: Dict = {
  "nav.account_panel": "Panneau d'administration",
  "nav.user_panel": "Espace utilisateur",
  "nav.logout": "Se déconnecter",
  "nav.choose_lang": "Choisir la langue",
  "login.title": "Se connecter",
  "login.subtitle": "Bon retour sur Fablio.",
  "login.google": "Continuer avec Google",
  "login.facebook": "Continuer avec Facebook",
  "login.or": "ou",
  "login.email": "E-mail",
  "login.password": "Mot de passe",
  "login.forgot": "Mot de passe oublié ?",
  "login.submit": "Se connecter",
  "login.submitting": "Connexion…",
  "login.no_account": "Vous n'avez pas de compte ?",
  "login.register": "Créer un compte",
  "login.error": "E-mail ou mot de passe incorrect",
  "register.title": "Créer un compte",
  "register.subtitle": "Vos histoires et abonnements au même endroit.",
  "register.name": "Prénom (facultatif)",
  "register.email": "E-mail",
  "register.password": "Mot de passe (min. 8 caractères)",
  "register.submit": "Créer un compte",
  "register.submitting": "Création du compte…",
  "register.has_account": "Vous avez déjà un compte ?",
  "register.login": "Se connecter",
  "forgot.title": "Réinitialiser le mot de passe",
  "forgot.subtitle":
    "Saisissez votre e-mail — nous vous enverrons un lien pour définir un nouveau mot de passe.",
  "forgot.email": "E-mail",
  "forgot.submit": "Envoyer le lien",
  "forgot.submitting": "Envoi…",
  "forgot.sent":
    "Si un compte existe avec cet e-mail, nous avons envoyé un lien de réinitialisation. Vérifiez votre boîte (et les spams).",
  "forgot.back": "← Retour à la connexion",
  "reset.title": "Nouveau mot de passe",
  "reset.no_token": "Jeton manquant dans le lien.",
  "reset.request_new": "Demandez-en un nouveau",
  "reset.changed": "✓ Votre mot de passe a été modifié.",
  "reset.login": "Se connecter",
  "reset.next": "Nouveau mot de passe (min. 8 caractères)",
  "reset.confirm": "Répétez le nouveau mot de passe",
  "reset.submit": "Définir le nouveau mot de passe",
  "reset.submitting": "Enregistrement…",
  "account.greeting": "Bonjour{name} ! 👋",
  "account.subtitle": "Vos histoires personnalisées au même endroit.",
  "account.my_stories": "Mes histoires",
  "account.no_stories": "Vous n'avez pas encore d'histoires.",
  "account.create_first": "✨ Créez votre première histoire",
  "account.listen": "▶ Écouter",
  "account.status.pending": "En attente de paiement",
  "account.status.paid": "En préparation",
  "account.status.fulfilled": "Prêt",
  "account.status.failed": "Erreur",
  "password.set_title": "Définir un mot de passe",
  "password.change_title": "Changer le mot de passe",
  "password.set_desc":
    "Votre compte a été créé avec Google. Définissez un mot de passe pour vous connecter aussi avec e-mail et mot de passe.",
  "password.change_desc":
    "Le mot de passe est stocké uniquement sous forme de hachage bcrypt.",
  "password.current": "Mot de passe actuel",
  "password.next": "Nouveau mot de passe (min. 8 caractères)",
  "password.confirm": "Répétez le nouveau mot de passe",
  "password.set_btn": "Définir le mot de passe",
  "password.change_btn": "Changer le mot de passe",
  "password.saving": "Enregistrement…",
  "password.saved": "✓ Mot de passe enregistré.",
  "errors.invalid_email": "E-mail invalide",
  "errors.password_min8": "Le mot de passe doit comporter au moins 8 caractères",
  "errors.email_exists": "Un compte avec cet e-mail existe déjà",
  "errors.passwords_mismatch": "Les mots de passe ne correspondent pas",
  "errors.no_token": "Jeton manquant",
  "errors.link_expired":
    "Le lien a expiré ou n'est pas valide. Demandez-en un nouveau.",
  "errors.no_session": "Aucune session",
  "errors.no_account": "Aucun compte",
  "errors.current_wrong": "Le mot de passe actuel est incorrect",
};

const it: Dict = {
  "nav.account_panel": "Pannello admin",
  "nav.user_panel": "Area utente",
  "nav.logout": "Esci",
  "nav.choose_lang": "Scegli la lingua",
  "login.title": "Accedi",
  "login.subtitle": "Bentornato su Fablio.",
  "login.google": "Continua con Google",
  "login.facebook": "Continua con Facebook",
  "login.or": "oppure",
  "login.email": "E-mail",
  "login.password": "Password",
  "login.forgot": "Password dimenticata?",
  "login.submit": "Accedi",
  "login.submitting": "Accesso…",
  "login.no_account": "Non hai un account?",
  "login.register": "Crea account",
  "login.error": "E-mail o password non corretti",
  "register.title": "Crea account",
  "register.subtitle": "Le tue storie e i tuoi abbonamenti in un unico posto.",
  "register.name": "Nome (facoltativo)",
  "register.email": "E-mail",
  "register.password": "Password (min. 8 caratteri)",
  "register.submit": "Crea account",
  "register.submitting": "Creazione account…",
  "register.has_account": "Hai già un account?",
  "register.login": "Accedi",
  "forgot.title": "Reimposta password",
  "forgot.subtitle":
    "Inserisci la tua e-mail: ti invieremo un link per impostare una nuova password.",
  "forgot.email": "E-mail",
  "forgot.submit": "Invia link",
  "forgot.submitting": "Invio…",
  "forgot.sent":
    "Se esiste un account con questa e-mail, abbiamo inviato un link per reimpostare la password. Controlla la posta (e lo spam).",
  "forgot.back": "← Torna all'accesso",
  "reset.title": "Nuova password",
  "reset.no_token": "Token mancante nel link.",
  "reset.request_new": "Richiedine uno nuovo",
  "reset.changed": "✓ La tua password è stata cambiata.",
  "reset.login": "Accedi",
  "reset.next": "Nuova password (min. 8 caratteri)",
  "reset.confirm": "Ripeti la nuova password",
  "reset.submit": "Imposta nuova password",
  "reset.submitting": "Salvataggio…",
  "account.greeting": "Ciao{name}! 👋",
  "account.subtitle": "Le tue storie personalizzate in un unico posto.",
  "account.my_stories": "Le mie storie",
  "account.no_stories": "Non hai ancora storie.",
  "account.create_first": "✨ Crea la tua prima storia",
  "account.listen": "▶ Ascolta",
  "account.status.pending": "In attesa di pagamento",
  "account.status.paid": "In preparazione",
  "account.status.fulfilled": "Pronto",
  "account.status.failed": "Errore",
  "password.set_title": "Imposta password",
  "password.change_title": "Cambia password",
  "password.set_desc":
    "Il tuo account è stato creato con Google. Imposta una password per accedere anche con e-mail e password.",
  "password.change_desc": "La password è memorizzata solo come hash bcrypt.",
  "password.current": "Password attuale",
  "password.next": "Nuova password (min. 8 caratteri)",
  "password.confirm": "Ripeti la nuova password",
  "password.set_btn": "Imposta password",
  "password.change_btn": "Cambia password",
  "password.saving": "Salvataggio…",
  "password.saved": "✓ Password salvata.",
  "errors.invalid_email": "E-mail non valida",
  "errors.password_min8": "La password deve avere almeno 8 caratteri",
  "errors.email_exists": "Esiste già un account con questa e-mail",
  "errors.passwords_mismatch": "Le password non coincidono",
  "errors.no_token": "Token mancante",
  "errors.link_expired": "Il link è scaduto o non valido. Richiedine uno nuovo.",
  "errors.no_session": "Nessuna sessione",
  "errors.no_account": "Nessun account",
  "errors.current_wrong": "La password attuale non è corretta",
};

const pt: Dict = {
  "nav.account_panel": "Painel de administração",
  "nav.user_panel": "Área do utilizador",
  "nav.logout": "Terminar sessão",
  "nav.choose_lang": "Escolher idioma",
  "login.title": "Iniciar sessão",
  "login.subtitle": "Bem-vindo de volta ao Fablio.",
  "login.google": "Continuar com o Google",
  "login.facebook": "Continuar com o Facebook",
  "login.or": "ou",
  "login.email": "E-mail",
  "login.password": "Palavra-passe",
  "login.forgot": "Esqueceu-se da palavra-passe?",
  "login.submit": "Iniciar sessão",
  "login.submitting": "A iniciar sessão…",
  "login.no_account": "Não tem conta?",
  "login.register": "Criar conta",
  "login.error": "E-mail ou palavra-passe incorretos",
  "register.title": "Criar conta",
  "register.subtitle": "As suas histórias e subscrições num só lugar.",
  "register.name": "Nome (opcional)",
  "register.email": "E-mail",
  "register.password": "Palavra-passe (mín. 8 caracteres)",
  "register.submit": "Criar conta",
  "register.submitting": "A criar conta…",
  "register.has_account": "Já tem conta?",
  "register.login": "Iniciar sessão",
  "forgot.title": "Repor palavra-passe",
  "forgot.subtitle":
    "Introduza o seu e-mail — enviaremos um link para definir uma nova palavra-passe.",
  "forgot.email": "E-mail",
  "forgot.submit": "Enviar link",
  "forgot.submitting": "A enviar…",
  "forgot.sent":
    "Se existir uma conta com este e-mail, enviámos um link para repor a palavra-passe. Verifique a caixa de entrada (e o spam).",
  "forgot.back": "← Voltar ao início de sessão",
  "reset.title": "Nova palavra-passe",
  "reset.no_token": "Falta o token no link.",
  "reset.request_new": "Peça um novo",
  "reset.changed": "✓ A sua palavra-passe foi alterada.",
  "reset.login": "Iniciar sessão",
  "reset.next": "Nova palavra-passe (mín. 8 caracteres)",
  "reset.confirm": "Repita a nova palavra-passe",
  "reset.submit": "Definir nova palavra-passe",
  "reset.submitting": "A guardar…",
  "account.greeting": "Olá{name}! 👋",
  "account.subtitle": "As suas histórias personalizadas num só lugar.",
  "account.my_stories": "As minhas histórias",
  "account.no_stories": "Ainda não tem histórias.",
  "account.create_first": "✨ Crie a sua primeira história",
  "account.listen": "▶ Ouvir",
  "account.status.pending": "A aguardar pagamento",
  "account.status.paid": "Em preparação",
  "account.status.fulfilled": "Pronto",
  "account.status.failed": "Erro",
  "password.set_title": "Definir palavra-passe",
  "password.change_title": "Alterar palavra-passe",
  "password.set_desc":
    "A sua conta foi criada com o Google. Defina uma palavra-passe para iniciar sessão também com e-mail e palavra-passe.",
  "password.change_desc": "A palavra-passe é guardada apenas como hash bcrypt.",
  "password.current": "Palavra-passe atual",
  "password.next": "Nova palavra-passe (mín. 8 caracteres)",
  "password.confirm": "Repita a nova palavra-passe",
  "password.set_btn": "Definir palavra-passe",
  "password.change_btn": "Alterar palavra-passe",
  "password.saving": "A guardar…",
  "password.saved": "✓ Palavra-passe guardada.",
  "errors.invalid_email": "E-mail inválido",
  "errors.password_min8": "A palavra-passe deve ter pelo menos 8 caracteres",
  "errors.email_exists": "Já existe uma conta com este e-mail",
  "errors.passwords_mismatch": "As palavras-passe não coincidem",
  "errors.no_token": "Token em falta",
  "errors.link_expired": "O link expirou ou é inválido. Peça um novo.",
  "errors.no_session": "Sem sessão",
  "errors.no_account": "Sem conta",
  "errors.current_wrong": "A palavra-passe atual está incorreta",
};

const nl: Dict = {
  "nav.account_panel": "Adminpaneel",
  "nav.user_panel": "Gebruikerspaneel",
  "nav.logout": "Afmelden",
  "nav.choose_lang": "Kies taal",
  "login.title": "Inloggen",
  "login.subtitle": "Welkom terug bij Fablio.",
  "login.google": "Doorgaan met Google",
  "login.facebook": "Doorgaan met Facebook",
  "login.or": "of",
  "login.email": "E-mail",
  "login.password": "Wachtwoord",
  "login.forgot": "Wachtwoord vergeten?",
  "login.submit": "Inloggen",
  "login.submitting": "Inloggen…",
  "login.no_account": "Nog geen account?",
  "login.register": "Account aanmaken",
  "login.error": "Onjuist e-mailadres of wachtwoord",
  "register.title": "Account aanmaken",
  "register.subtitle": "Je verhalen en abonnementen op één plek.",
  "register.name": "Naam (optioneel)",
  "register.email": "E-mail",
  "register.password": "Wachtwoord (min. 8 tekens)",
  "register.submit": "Account aanmaken",
  "register.submitting": "Account aanmaken…",
  "register.has_account": "Heb je al een account?",
  "register.login": "Inloggen",
  "forgot.title": "Wachtwoord opnieuw instellen",
  "forgot.subtitle":
    "Voer je e-mail in — we sturen je een link om een nieuw wachtwoord in te stellen.",
  "forgot.email": "E-mail",
  "forgot.submit": "Link versturen",
  "forgot.submitting": "Versturen…",
  "forgot.sent":
    "Als er een account met dit e-mailadres bestaat, hebben we een link gestuurd om je wachtwoord opnieuw in te stellen. Controleer je inbox (en spam).",
  "forgot.back": "← Terug naar inloggen",
  "reset.title": "Nieuw wachtwoord",
  "reset.no_token": "Token ontbreekt in de link.",
  "reset.request_new": "Vraag een nieuwe aan",
  "reset.changed": "✓ Je wachtwoord is gewijzigd.",
  "reset.login": "Inloggen",
  "reset.next": "Nieuw wachtwoord (min. 8 tekens)",
  "reset.confirm": "Herhaal nieuw wachtwoord",
  "reset.submit": "Nieuw wachtwoord instellen",
  "reset.submitting": "Opslaan…",
  "account.greeting": "Hoi{name}! 👋",
  "account.subtitle": "Je gepersonaliseerde verhalen op één plek.",
  "account.my_stories": "Mijn verhalen",
  "account.no_stories": "Je hebt nog geen verhalen.",
  "account.create_first": "✨ Maak je eerste verhaal",
  "account.listen": "▶ Luisteren",
  "account.status.pending": "Wacht op betaling",
  "account.status.paid": "In voorbereiding",
  "account.status.fulfilled": "Klaar",
  "account.status.failed": "Fout",
  "password.set_title": "Wachtwoord instellen",
  "password.change_title": "Wachtwoord wijzigen",
  "password.set_desc":
    "Je account is aangemaakt met Google. Stel een wachtwoord in om ook met e-mail en wachtwoord in te loggen.",
  "password.change_desc": "Het wachtwoord wordt alleen als bcrypt-hash opgeslagen.",
  "password.current": "Huidig wachtwoord",
  "password.next": "Nieuw wachtwoord (min. 8 tekens)",
  "password.confirm": "Herhaal nieuw wachtwoord",
  "password.set_btn": "Wachtwoord instellen",
  "password.change_btn": "Wachtwoord wijzigen",
  "password.saving": "Opslaan…",
  "password.saved": "✓ Wachtwoord opgeslagen.",
  "errors.invalid_email": "Ongeldig e-mailadres",
  "errors.password_min8": "Wachtwoord moet minstens 8 tekens bevatten",
  "errors.email_exists": "Er bestaat al een account met dit e-mailadres",
  "errors.passwords_mismatch": "Wachtwoorden komen niet overeen",
  "errors.no_token": "Token ontbreekt",
  "errors.link_expired": "De link is verlopen of ongeldig. Vraag een nieuwe aan.",
  "errors.no_session": "Geen sessie",
  "errors.no_account": "Geen account",
  "errors.current_wrong": "Huidig wachtwoord is onjuist",
};

const uk: Dict = {
  "nav.account_panel": "Панель адміністратора",
  "nav.user_panel": "Панель користувача",
  "nav.logout": "Вийти",
  "nav.choose_lang": "Виберіть мову",
  "login.title": "Увійти",
  "login.subtitle": "З поверненням до Fablio.",
  "login.google": "Продовжити з Google",
  "login.facebook": "Продовжити з Facebook",
  "login.or": "або",
  "login.email": "Електронна пошта",
  "login.password": "Пароль",
  "login.forgot": "Забули пароль?",
  "login.submit": "Увійти",
  "login.submitting": "Вхід…",
  "login.no_account": "Немає облікового запису?",
  "login.register": "Створити обліковий запис",
  "login.error": "Неправильна пошта або пароль",
  "register.title": "Створити обліковий запис",
  "register.subtitle": "Ваші казки та підписки в одному місці.",
  "register.name": "Ім'я (необов'язково)",
  "register.email": "Електронна пошта",
  "register.password": "Пароль (мін. 8 символів)",
  "register.submit": "Створити обліковий запис",
  "register.submitting": "Створення облікового запису…",
  "register.has_account": "Вже маєте обліковий запис?",
  "register.login": "Увійти",
  "forgot.title": "Скидання пароля",
  "forgot.subtitle":
    "Введіть свою пошту — ми надішлемо посилання для встановлення нового пароля.",
  "forgot.email": "Електронна пошта",
  "forgot.submit": "Надіслати посилання",
  "forgot.submitting": "Надсилання…",
  "forgot.sent":
    "Якщо обліковий запис із цією поштою існує, ми надіслали посилання для скидання пароля. Перевірте пошту (і спам).",
  "forgot.back": "← Назад до входу",
  "reset.title": "Новий пароль",
  "reset.no_token": "У посиланні немає токена.",
  "reset.request_new": "Запросіть новий",
  "reset.changed": "✓ Ваш пароль змінено.",
  "reset.login": "Увійти",
  "reset.next": "Новий пароль (мін. 8 символів)",
  "reset.confirm": "Повторіть новий пароль",
  "reset.submit": "Встановити новий пароль",
  "reset.submitting": "Збереження…",
  "account.greeting": "Привіт{name}! 👋",
  "account.subtitle": "Ваші персоналізовані казки в одному місці.",
  "account.my_stories": "Мої казки",
  "account.no_stories": "У вас ще немає казок.",
  "account.create_first": "✨ Створіть першу казку",
  "account.listen": "▶ Слухати",
  "account.status.pending": "Очікує оплати",
  "account.status.paid": "Готується",
  "account.status.fulfilled": "Готово",
  "account.status.failed": "Помилка",
  "password.set_title": "Встановити пароль",
  "password.change_title": "Змінити пароль",
  "password.set_desc":
    "Ваш обліковий запис створено через Google. Встановіть пароль, щоб входити також за допомогою пошти та пароля.",
  "password.change_desc": "Пароль зберігається лише як bcrypt-хеш.",
  "password.current": "Поточний пароль",
  "password.next": "Новий пароль (мін. 8 символів)",
  "password.confirm": "Повторіть новий пароль",
  "password.set_btn": "Встановити пароль",
  "password.change_btn": "Змінити пароль",
  "password.saving": "Збереження…",
  "password.saved": "✓ Пароль збережено.",
  "errors.invalid_email": "Недійсна електронна пошта",
  "errors.password_min8": "Пароль має містити щонайменше 8 символів",
  "errors.email_exists": "Обліковий запис із цією поштою вже існує",
  "errors.passwords_mismatch": "Паролі не збігаються",
  "errors.no_token": "Немає токена",
  "errors.link_expired": "Посилання застаріло або недійсне. Запросіть нове.",
  "errors.no_session": "Немає сесії",
  "errors.no_account": "Немає облікового запису",
  "errors.current_wrong": "Поточний пароль неправильний",
};

const sv: Dict = {
  "nav.account_panel": "Adminpanel",
  "nav.user_panel": "Användarpanel",
  "nav.logout": "Logga ut",
  "nav.choose_lang": "Välj språk",
  "login.title": "Logga in",
  "login.subtitle": "Välkommen tillbaka till Fablio.",
  "login.google": "Fortsätt med Google",
  "login.facebook": "Fortsätt med Facebook",
  "login.or": "eller",
  "login.email": "E-post",
  "login.password": "Lösenord",
  "login.forgot": "Glömt lösenordet?",
  "login.submit": "Logga in",
  "login.submitting": "Loggar in…",
  "login.no_account": "Har du inget konto?",
  "login.register": "Skapa konto",
  "login.error": "Fel e-post eller lösenord",
  "register.title": "Skapa konto",
  "register.subtitle": "Dina sagor och prenumerationer på ett ställe.",
  "register.name": "Namn (valfritt)",
  "register.email": "E-post",
  "register.password": "Lösenord (min. 8 tecken)",
  "register.submit": "Skapa konto",
  "register.submitting": "Skapar konto…",
  "register.has_account": "Har du redan ett konto?",
  "register.login": "Logga in",
  "forgot.title": "Återställ lösenord",
  "forgot.subtitle":
    "Ange din e-post — vi skickar en länk för att ange ett nytt lösenord.",
  "forgot.email": "E-post",
  "forgot.submit": "Skicka länk",
  "forgot.submitting": "Skickar…",
  "forgot.sent":
    "Om ett konto med denna e-post finns har vi skickat en länk för att återställa lösenordet. Kontrollera din inkorg (och skräppost).",
  "forgot.back": "← Tillbaka till inloggning",
  "reset.title": "Nytt lösenord",
  "reset.no_token": "Token saknas i länken.",
  "reset.request_new": "Begär en ny",
  "reset.changed": "✓ Ditt lösenord har ändrats.",
  "reset.login": "Logga in",
  "reset.next": "Nytt lösenord (min. 8 tecken)",
  "reset.confirm": "Upprepa nytt lösenord",
  "reset.submit": "Ange nytt lösenord",
  "reset.submitting": "Sparar…",
  "account.greeting": "Hej{name}! 👋",
  "account.subtitle": "Dina personliga sagor på ett ställe.",
  "account.my_stories": "Mina sagor",
  "account.no_stories": "Du har inga sagor ännu.",
  "account.create_first": "✨ Skapa din första saga",
  "account.listen": "▶ Lyssna",
  "account.status.pending": "Väntar på betalning",
  "account.status.paid": "Förbereds",
  "account.status.fulfilled": "Klar",
  "account.status.failed": "Fel",
  "password.set_title": "Ange lösenord",
  "password.change_title": "Ändra lösenord",
  "password.set_desc":
    "Ditt konto skapades med Google. Ange ett lösenord för att även logga in med e-post och lösenord.",
  "password.change_desc": "Lösenordet lagras endast som bcrypt-hash.",
  "password.current": "Nuvarande lösenord",
  "password.next": "Nytt lösenord (min. 8 tecken)",
  "password.confirm": "Upprepa nytt lösenord",
  "password.set_btn": "Ange lösenord",
  "password.change_btn": "Ändra lösenord",
  "password.saving": "Sparar…",
  "password.saved": "✓ Lösenord sparat.",
  "errors.invalid_email": "Ogiltig e-post",
  "errors.password_min8": "Lösenordet måste vara minst 8 tecken",
  "errors.email_exists": "Ett konto med denna e-post finns redan",
  "errors.passwords_mismatch": "Lösenorden matchar inte",
  "errors.no_token": "Token saknas",
  "errors.link_expired": "Länken har upphört eller är ogiltig. Begär en ny.",
  "errors.no_session": "Ingen session",
  "errors.no_account": "Inget konto",
  "errors.current_wrong": "Nuvarande lösenord är felaktigt",
};

const cs: Dict = {
  "nav.account_panel": "Panel administrátora",
  "nav.user_panel": "Panel uživatele",
  "nav.logout": "Odhlásit se",
  "nav.choose_lang": "Vyberte jazyk",
  "login.title": "Přihlásit se",
  "login.subtitle": "Vítejte zpět ve Fablio.",
  "login.google": "Pokračovat přes Google",
  "login.facebook": "Pokračovat přes Facebook",
  "login.or": "nebo",
  "login.email": "E-mail",
  "login.password": "Heslo",
  "login.forgot": "Zapomněli jste heslo?",
  "login.submit": "Přihlásit se",
  "login.submitting": "Přihlašování…",
  "login.no_account": "Nemáte účet?",
  "login.register": "Vytvořit účet",
  "login.error": "Nesprávný e-mail nebo heslo",
  "register.title": "Vytvořit účet",
  "register.subtitle": "Vaše pohádky a předplatná na jednom místě.",
  "register.name": "Jméno (nepovinné)",
  "register.email": "E-mail",
  "register.password": "Heslo (min. 8 znaků)",
  "register.submit": "Vytvořit účet",
  "register.submitting": "Vytváření účtu…",
  "register.has_account": "Už máte účet?",
  "register.login": "Přihlásit se",
  "forgot.title": "Obnovení hesla",
  "forgot.subtitle":
    "Zadejte svůj e-mail — pošleme vám odkaz pro nastavení nového hesla.",
  "forgot.email": "E-mail",
  "forgot.submit": "Odeslat odkaz",
  "forgot.submitting": "Odesílání…",
  "forgot.sent":
    "Pokud účet s tímto e-mailem existuje, poslali jsme odkaz pro obnovení hesla. Zkontrolujte schránku (i spam).",
  "forgot.back": "← Zpět na přihlášení",
  "reset.title": "Nové heslo",
  "reset.no_token": "V odkazu chybí token.",
  "reset.request_new": "Požádejte o nový",
  "reset.changed": "✓ Vaše heslo bylo změněno.",
  "reset.login": "Přihlásit se",
  "reset.next": "Nové heslo (min. 8 znaků)",
  "reset.confirm": "Zopakujte nové heslo",
  "reset.submit": "Nastavit nové heslo",
  "reset.submitting": "Ukládání…",
  "account.greeting": "Ahoj{name}! 👋",
  "account.subtitle": "Vaše personalizované pohádky na jednom místě.",
  "account.my_stories": "Moje pohádky",
  "account.no_stories": "Zatím nemáte žádné pohádky.",
  "account.create_first": "✨ Vytvořte první pohádku",
  "account.listen": "▶ Poslechnout",
  "account.status.pending": "Čeká na platbu",
  "account.status.paid": "Připravuje se",
  "account.status.fulfilled": "Hotovo",
  "account.status.failed": "Chyba",
  "password.set_title": "Nastavit heslo",
  "password.change_title": "Změnit heslo",
  "password.set_desc":
    "Váš účet byl vytvořen přes Google. Nastavte heslo, abyste se mohli přihlašovat i e-mailem a heslem.",
  "password.change_desc": "Heslo je uloženo pouze jako bcrypt-hash.",
  "password.current": "Současné heslo",
  "password.next": "Nové heslo (min. 8 znaků)",
  "password.confirm": "Zopakujte nové heslo",
  "password.set_btn": "Nastavit heslo",
  "password.change_btn": "Změnit heslo",
  "password.saving": "Ukládání…",
  "password.saved": "✓ Heslo uloženo.",
  "errors.invalid_email": "Neplatný e-mail",
  "errors.password_min8": "Heslo musí mít alespoň 8 znaků",
  "errors.email_exists": "Účet s tímto e-mailem už existuje",
  "errors.passwords_mismatch": "Hesla se neshodují",
  "errors.no_token": "Chybí token",
  "errors.link_expired": "Odkaz vypršel nebo je neplatný. Požádejte o nový.",
  "errors.no_session": "Žádná relace",
  "errors.no_account": "Žádný účet",
  "errors.current_wrong": "Současné heslo je nesprávné",
};

const tr: Dict = {
  "nav.account_panel": "Yönetici paneli",
  "nav.user_panel": "Kullanıcı paneli",
  "nav.logout": "Çıkış yap",
  "nav.choose_lang": "Dil seçin",
  "login.title": "Giriş yap",
  "login.subtitle": "Fablio'ya tekrar hoş geldin.",
  "login.google": "Google ile devam et",
  "login.facebook": "Facebook ile devam et",
  "login.or": "veya",
  "login.email": "E-posta",
  "login.password": "Parola",
  "login.forgot": "Parolanı mı unuttun?",
  "login.submit": "Giriş yap",
  "login.submitting": "Giriş yapılıyor…",
  "login.no_account": "Hesabın yok mu?",
  "login.register": "Hesap oluştur",
  "login.error": "Hatalı e-posta veya parola",
  "register.title": "Hesap oluştur",
  "register.subtitle": "Masalların ve aboneliklerin tek bir yerde.",
  "register.name": "Ad (isteğe bağlı)",
  "register.email": "E-posta",
  "register.password": "Parola (en az 8 karakter)",
  "register.submit": "Hesap oluştur",
  "register.submitting": "Hesap oluşturuluyor…",
  "register.has_account": "Zaten hesabın var mı?",
  "register.login": "Giriş yap",
  "forgot.title": "Parolayı sıfırla",
  "forgot.subtitle":
    "E-postanı gir — yeni bir parola belirlemen için bir bağlantı göndereceğiz.",
  "forgot.email": "E-posta",
  "forgot.submit": "Bağlantı gönder",
  "forgot.submitting": "Gönderiliyor…",
  "forgot.sent":
    "Bu e-postaya sahip bir hesap varsa, parola sıfırlama bağlantısı gönderdik. Gelen kutunu (ve spam'i) kontrol et.",
  "forgot.back": "← Girişe dön",
  "reset.title": "Yeni parola",
  "reset.no_token": "Bağlantıda token eksik.",
  "reset.request_new": "Yeni bir tane iste",
  "reset.changed": "✓ Parolan değiştirildi.",
  "reset.login": "Giriş yap",
  "reset.next": "Yeni parola (en az 8 karakter)",
  "reset.confirm": "Yeni parolayı tekrarla",
  "reset.submit": "Yeni parolayı belirle",
  "reset.submitting": "Kaydediliyor…",
  "account.greeting": "Merhaba{name}! 👋",
  "account.subtitle": "Kişiselleştirilmiş masalların tek bir yerde.",
  "account.my_stories": "Masallarım",
  "account.no_stories": "Henüz hiç masalın yok.",
  "account.create_first": "✨ İlk masalını oluştur",
  "account.listen": "▶ Dinle",
  "account.status.pending": "Ödeme bekleniyor",
  "account.status.paid": "Hazırlanıyor",
  "account.status.fulfilled": "Hazır",
  "account.status.failed": "Hata",
  "password.set_title": "Parola belirle",
  "password.change_title": "Parola değiştir",
  "password.set_desc":
    "Hesabın Google ile oluşturuldu. E-posta ve parolayla da giriş yapabilmek için bir parola belirle.",
  "password.change_desc": "Parola yalnızca bcrypt-hash olarak saklanır.",
  "password.current": "Mevcut parola",
  "password.next": "Yeni parola (en az 8 karakter)",
  "password.confirm": "Yeni parolayı tekrarla",
  "password.set_btn": "Parola belirle",
  "password.change_btn": "Parola değiştir",
  "password.saving": "Kaydediliyor…",
  "password.saved": "✓ Parola kaydedildi.",
  "errors.invalid_email": "Geçersiz e-posta",
  "errors.password_min8": "Parola en az 8 karakter olmalı",
  "errors.email_exists": "Bu e-postaya sahip bir hesap zaten var",
  "errors.passwords_mismatch": "Parolalar eşleşmiyor",
  "errors.no_token": "Token eksik",
  "errors.link_expired": "Bağlantının süresi doldu veya geçersiz. Yeni bir tane iste.",
  "errors.no_session": "Oturum yok",
  "errors.no_account": "Hesap yok",
  "errors.current_wrong": "Mevcut parola yanlış",
};

const DICT: Record<Lang, Dict> = {
  en,
  pl,
  de,
  es,
  fr,
  it,
  pt,
  nl,
  uk,
  sv,
  cs,
  tr,
};

export function t(
  lang: Lang,
  key: string,
  vars?: Record<string, string>,
): string {
  let s = DICT[lang]?.[key] ?? DICT[DEFAULT_LANG][key] ?? key;
  if (vars) {
    for (const k of Object.keys(vars)) {
      s = s.split(`{${k}}`).join(vars[k]);
    }
  }
  return s;
}
